// Layout variant: v4-segmented-mode (Key | Tag switcher, split panel with instructions, max-width)
import React, { useEffect, useRef, useState } from 'react';
import KwPanel from '../common/KwPanel';
import ActionBar from '../layout/ActionBar';
import KeySearchKeysField from './components/KeySearchKeysField';
import KeySearchTagField from './components/KeySearchTagField';
import KeySearchModeSwitcher, {
  type KeySearchMode,
} from './components/KeySearchModeSwitcher';
import KeySearchInstructions from './components/KeySearchInstructions';
import SearchCodeList from './components/SearchCodeList';
import SearchTagList from './components/SearchTagList';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';

function countKeyLines(text: string): number {
  return text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).length;
}

function KeySearch() {
  const { t } = useTranslation();
  usePageTitle(['Search', 'Keys']);

  const [reload] = useState(undefined);
  const [searchMode, setSearchMode] = useLocalStorage<KeySearchMode>(
    'keys-search-mode',
    'code'
  );
  const [searchTerms, setSearchTerms] = useState<{
    mode?: string;
    code?: string;
    tag?: string;
    bulkSearch?: boolean;
  }>();
  const {
    register,
    formState: { errors },
  } = useForm();
  const [keyform, setKeyForm] = useLocalStorage<{
    mode: string;
    code: string;
    tag: string;
  }>('keys-settings', {
    mode: '',
    code: '',
    tag: '',
  });
  const keyInputRef = useRef<HTMLTextAreaElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (searchMode === 'code') {
        keyInputRef.current?.focus();
      } else {
        tagInputRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [searchMode]);

  const handleModeChange = (mode: KeySearchMode) => {
    setSearchMode(mode);
    setSearchTerms(undefined);
  };

  const handleChange = ({
    target,
  }: {
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
  }) => {
    setSearchTerms(undefined);
    setKeyForm({
      ...keyform,
      [target.name]: target.value,
    });
  };

  const searchKey = () => {
    const lineCount = countKeyLines(keyform.code);
    if (lineCount === 0) return;

    setSearchTerms({
      ...keyform,
      mode: 'code',
      bulkSearch: lineCount > 1,
    });
  };

  const searchTag = () => {
    if (!keyform.tag?.trim()) return;

    setSearchTerms({
      ...keyform,
      mode: 'tag',
    });
  };

  return (
    <>
      <ActionBar />
      <KwPanel className="pb-3 pt-1">
        <div className="key-search-layout">
          <KeySearchModeSwitcher
            mode={searchMode}
            onModeChange={handleModeChange}
          />
          <div className="key-search-fields-row">
            <div className="key-search-fields-input">
              {searchMode === 'code' ? (
                <KeySearchKeysField
                  ref={keyInputRef}
                  name="code"
                  label={t('Key')}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  defaultValue={keyform.code || ''}
                  onChange={handleChange}
                  error={errors.code}
                  searchButtonLabel={t('Search')}
                  onSearch={searchKey}
                  searchDisabled={
                    !keyform.code || searchTerms?.mode === 'code'
                  }
                />
              ) : (
                <KeySearchTagField
                  ref={tagInputRef}
                  register={register}
                  name="tag"
                  label={t('Tag')}
                  placeholder="XXX"
                  defaultValue={keyform.tag || ''}
                  onChange={handleChange}
                  error={errors.tag}
                  searchButtonLabel={t('Search')}
                  onSearch={searchTag}
                  searchDisabled={
                    !keyform.tag || searchTerms?.mode === 'tag'
                  }
                />
              )}
            </div>
            <div className="key-search-fields-instructions">
              <KeySearchInstructions mode={searchMode} />
            </div>
          </div>
        </div>
      </KwPanel>

      {searchTerms && searchTerms.mode === 'code' && (
        <SearchCodeList
          reload={reload}
          code={searchTerms.bulkSearch ? undefined : searchTerms.code}
          codesText={searchTerms.bulkSearch ? searchTerms.code : undefined}
          bulkSearch={searchTerms.bulkSearch}
        />
      )}

      {searchTerms && searchTerms.mode === 'tag' && (
        <SearchTagList reload={reload} tag={searchTerms.tag} />
      )}
    </>
  );
}

export default KeySearch;
